import type { Review } from '~/composables/types/event'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import { convertISO2Locale } from '~/utils/localeHelpers'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'

export type EventReviewsOrderBy = 'createdAt' | 'rating'

export type EventReviewsSortedBy = 'asc' | 'desc'

export interface EventReviewsParams {
  search?: string
  orderBy?: EventReviewsOrderBy
  sortedBy?: EventReviewsSortedBy
  limit?: number
}

export interface UseEventReviewsFetchOptions {
  eventPermalink?: MaybeRefOrGetter<string | undefined>
}

const DEFAULT_LIMIT = 20
const DEFAULT_ORDER_BY: EventReviewsOrderBy = 'createdAt'
const DEFAULT_SORTED_BY: EventReviewsSortedBy = 'desc'

const emptyReviews = () => [] as Review[]
const noopRefresh = () => Promise.resolve()

export const useEventReviewsFetch = (
  initial: Partial<EventReviewsParams> = {},
  options: UseEventReviewsFetchOptions = {}
) => {
  const search = ref<string | undefined>(initial.search)
  const orderBy = ref<EventReviewsOrderBy>(
    initial.orderBy ?? DEFAULT_ORDER_BY
  )
  const sortedBy = ref<EventReviewsSortedBy>(
    initial.sortedBy ?? DEFAULT_SORTED_BY
  )
  const limit = ref<number>(initial.limit ?? DEFAULT_LIMIT)

  const queryParams = computed<Record<string, string | number>>(() => {
    const params: Record<string, string | number> = {
      orderBy: orderBy.value,
      sortedBy: sortedBy.value,
      limit: limit.value,
      include: 'media'
    }
    if (search.value) {
      params.search = search.value
    }
    return params
  })

  const setRatingFilter = (rating: number | null) => {
    if (rating === null) {
      search.value = undefined
      return
    }
    search.value = `rating:${rating}`
  }

  const setOrder = (by: EventReviewsOrderBy, direction: EventReviewsSortedBy) => {
    orderBy.value = by
    sortedBy.value = direction
  }

  const reset = () => {
    search.value = initial.search
    orderBy.value = initial.orderBy ?? DEFAULT_ORDER_BY
    sortedBy.value = initial.sortedBy ?? DEFAULT_SORTED_BY
    limit.value = initial.limit ?? DEFAULT_LIMIT
  }

  const eventPermalink = options.eventPermalink

  if (!eventPermalink) {
    const reviews = computed(emptyReviews)
    const pending = readonly(ref(false))
    const error = readonly(ref(null))
    return {
      search,
      orderBy,
      sortedBy,
      limit,
      queryParams,
      setRatingFilter,
      setOrder,
      reset,
      reviews,
      pending,
      error,
      refreshReviews: noopRefresh,
    }
  }

  const config = useRuntimeConfig()
  const { apiBaseURL } = config.public
  const { locale: ISOLocale } = useI18n()
  const locale = convertISO2Locale(ISOLocale.value)
  const localeBCP47 = convertLocaleIntoBCP47(ISOLocale.value)

  const permalinkValue = computed(() => toValue(eventPermalink))
  const fetchKey = computed(() => {
    const permalink = permalinkValue.value
    if (!permalink) return 'model-reviews-disabled'
    return `model-reviews-${permalink}-${JSON.stringify(queryParams.value)}`
  })

  const {
    data: reviewsData,
    pending: reviewsPending,
    error: reviewsError,
    refresh: refreshReviews,
  } = useAsyncData(
    fetchKey,
    async () => {
      const permalink = permalinkValue.value
      if (!permalink) return []

      const response = await $fetch<KftApiDefaultResponse<Review[]>>(
        `/v1/store/model-reviews/${permalink}/`,
        {
          baseURL: apiBaseURL,
          query: queryParams.value,
          headers: {
            'Accept-Language': `${localeBCP47},${locale}`,
          },
        }
      )
      return Array.isArray(response?.data) ? response.data : []
    },
    {
      default: emptyReviews,
      watch: [search, orderBy, sortedBy, limit, permalinkValue],
    }
  )

  const reviews = computed(() => reviewsData.value ?? [])
  const pending = readonly(reviewsPending)
  const error = readonly(reviewsError)

  return {
    search,
    orderBy,
    sortedBy,
    limit,
    queryParams,
    setRatingFilter,
    setOrder,
    reset,
    reviews,
    pending,
    error,
    refreshReviews,
  }
}
